// Explore → "Add to dashboard…" (doc 05 §4): the signals on screen become a trend widget on a
// dashboard the user may edit. The dashboard is saved as a new version, like any other edit.
import { Alert, Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import type { Dashboard, Signal, Widget } from '@twinlink/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { dashKeys, putDashboard, useDashboards } from '@web/features/dashboards/api';
import { pasteWidget } from '@web/features/dashboards/model';
import { api } from '@web/lib/api';
import { errorMessage, notifySuccess } from '@web/lib/errors';

interface Props {
  orgId: string;
  opened: boolean;
  signals: Signal[];
  /** Unit chosen per signal in the exploration. */
  units: Record<string, string>;
  onClose: () => void;
}

export function AddToDashboard({ orgId, opened, signals, units, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dashboards = useDashboards(orgId);
  const editable = (dashboards.data ?? []).filter((d) => d.canEdit);
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [title, setTitle] = useState('');

  const add = useMutation({
    mutationFn: async () => {
      // Read the current version, add the widget on its first page, save on top of that version.
      const current = await api<Dashboard>(`/orgs/${orgId}/dashboards/${dashboardId}`);
      const widget: Widget = {
        type: 'trend',
        title: title.trim(),
        config: { series: signals.map((s, i) => ({ refId: `s${i}`, binding: { kind: 'signal', signalId: s.id }, displayUnitId: units[s.id] ?? null })), thresholds: [], legend: true, annotations: true },
      };
      const page = current.doc.pages[0]!;
      const { doc } = pasteWidget(current.doc, page.id, widget, { w: 12, h: 8 });
      return putDashboard(orgId, current.id, { doc, version: current.version, note: t('explorer.addTo.note') });
    },
    onSuccess: (saved) => {
      qc.setQueryData(dashKeys.one(orgId, saved.id), saved);
      notifySuccess(t('explorer.addTo.done', { name: saved.name }));
      onClose();
      navigate(`/o/${orgId}/dashboards/${saved.id}`);
    },
  });

  useEffect(() => {
    if (!opened) return;
    add.reset();
    setDashboardId(null);
    setTitle(signals.length === 1 ? signals[0]!.name : '');
    // Reset the form each time the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={t('explorer.addTo.title')}>
      <Stack gap="md">
        {add.isError && <Alert color="red">{errorMessage(add.error)}</Alert>}
        <Text size="sm" c="dimmed">
          {t('explorer.addTo.hint', { count: signals.length })}
        </Text>
        <Select
          label={t('explorer.addTo.dashboard')}
          data={editable.map((d) => ({ value: d.id, label: d.name }))}
          value={dashboardId}
          onChange={setDashboardId}
          searchable
          nothingFoundMessage={t('explorer.addTo.none')}
          data-autofocus
        />
        <TextInput label={t('dash.settings.widgetTitle')} value={title} maxLength={120} onChange={(e) => setTitle(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!dashboardId || signals.length === 0} loading={add.isPending} onClick={() => add.mutate()}>
            {t('explorer.addTo.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
